import React, { useEffect, useMemo, useRef, useState } from "react";

const DAY_OPTIONS = [
  { value: "Lun", label: "Lun" },
  { value: "Mar", label: "Mar" },
  { value: "Mie", label: "Mie" },
  { value: "Jue", label: "Jue" },
  { value: "Vie", label: "Vie" },
  { value: "Sab", label: "Sab" },
  { value: "Dom", label: "Dom" },
];

const DAY_ORDER = DAY_OPTIONS.map((d) => d.value);
const toTime = (value) => (value || "").slice(0, 5);
const serializeDays = (list = []) => list.join("-");
const SHIFT_OPTIONS = [
  { value: "Matutino", label: "Matutino" },
  { value: "Diurno", label: "Diurno" },
  { value: "Vespertino", label: "Vespertino" },
];

export default function CursosHorariosPanel({
  horarios = [],
  loading = false,
  saving = false,
  deleting = false,
  error = null,
  onCrear,
  onActualizar,
  onEliminar,
}) {
  const [form, setForm] = useState({
    nombre: "",
    hora_inicio: "",
    hora_fin: "",
    dias_semana: "",
  });
  const [selectedDays, setSelectedDays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuRef = useRef(null);

  const canSubmit = useMemo(
    () => form.nombre.trim() && form.hora_inicio && form.hora_fin && selectedDays.length > 0,
    [form.nombre, form.hora_inicio, form.hora_fin, selectedDays]
  );

  const toggleDay = (value) => {
    setSelectedDays((prev) => {
      const exists = prev.includes(value);
      const next = exists ? prev.filter((d) => d !== value) : [...prev, value];
      const ordered = next.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
      setForm((p) => ({ ...p, dias_semana: serializeDays(ordered) }));
      return ordered;
    });
  };

  const handleCrear = () => {
    if (!canSubmit) return;
    const payload = { ...form, dias_semana: serializeDays(selectedDays) };
    if (editingId) {
      onActualizar?.(editingId, payload);
    } else {
      onCrear?.(payload);
    }
    setForm({ nombre: "", hora_inicio: "", hora_fin: "", dias_semana: "" });
    setSelectedDays([]);
    setEditingId(null);
    setShowForm(false);
  };

  const resetForm = () => {
    setForm({ nombre: "", hora_inicio: "", hora_fin: "", dias_semana: "" });
    setSelectedDays([]);
    setEditingId(null);
  };

  const parseDays = (value) =>
    (value || "")
      .toString()
      .split("-")
      .map((d) => d.trim())
      .filter(Boolean);

  const handleAddClick = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditClick = (horario) => {
    const dias = parseDays(horario.diasSemana || horario.dias_semana || "");
    setForm({
      nombre: horario.nombre || "",
      hora_inicio: toTime(horario.horaInicio || horario.hora_inicio),
      hora_fin: toTime(horario.horaFin || horario.hora_fin),
      dias_semana: serializeDays(dias),
    });
    setSelectedDays(dias);
    setEditingId(horario.id);
    setShowForm(true);
    setMenuOpenId(null);
  };

  const openRowMenu = (event, rowId) => {
    event.preventDefault();
    event.stopPropagation();
    if (menuOpenId === rowId) {
      setMenuOpenId(null);
      setMenuPosition(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 180;
    const menuHeight = 84;
    const viewportPadding = 12;
    const gap = 8;
    let left = rect.right - menuWidth;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - menuWidth - viewportPadding));
    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = rect.top - menuHeight - gap;
    }
    setMenuOpenId(rowId);
    setMenuPosition({ top, left });
  };

  useEffect(() => {
    if (!menuOpenId) return undefined;
    const handleClick = (event) => {
      const target = event.target;
      const inMenu = menuRef.current?.contains(target);
      const fromTrigger = target instanceof Element && target.closest(".horarios-menu-trigger");
      if (inMenu || fromTrigger) return;
      setMenuOpenId(null);
      setMenuPosition(null);
    };
    document.addEventListener("click", handleClick);
    window.addEventListener("scroll", handleClick, true);
    window.addEventListener("resize", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
      window.removeEventListener("scroll", handleClick, true);
      window.removeEventListener("resize", handleClick);
    };
  }, [menuOpenId]);

  /* const diasPreview = selectedDays.length ? serializeDays(selectedDays) : "Sin dias seleccionados";
 */
  return (
    <div className="management-card mt-3 horarios-panel">
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h6 className="mb-1">Definir horarios del curso</h6>
          <p className="text-muted mb-0">Agrega los horarios disponibles para el curso</p>
        </div>
        <button type="button" className="btn btn-sm management-btn-primary" onClick={handleAddClick}>
          <i className="bi bi-plus-circle me-2" />
          Agregar horario
        </button>
      </div>

      {error && <div className="alert alert-warning py-2 mb-3">{error}</div>}

      {loading ? (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <>
          <div className="table-responsive mb-3">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Dias</th>
                  <th className="text-end">Accion</th>
                </tr>
              </thead>
              <tbody>
                {horarios.length ? (
                  horarios.map((h) => (
                    <tr key={h.id}>
                      <td className="fw-semibold">{h.nombre}</td>
                      <td>{toTime(h.horaInicio || h.hora_inicio)}</td>
                      <td>{toTime(h.horaFin || h.hora_fin)}</td>
                      <td>{h.diasSemana || h.dias_semana || "N/D"}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-muted horarios-menu-trigger"
                          onClick={(event) => openRowMenu(event, h.id)}
                          aria-label="Acciones"
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      Aun no hay horarios. Selecciona dias y horas para crear el primero.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {menuOpenId && menuPosition && (
            <div
              ref={menuRef}
              className="horarios-quick-menu"
              style={{ top: menuPosition.top, left: menuPosition.left, zIndex: 2300 }}
            >
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  const horario = horarios.find((item) => String(item.id) === String(menuOpenId));
                  if (horario) handleEditClick(horario);
                  setMenuOpenId(null);
                  setMenuPosition(null);
                }}
              >
                <i className="bi bi-pencil me-2" />
                Editar
              </button>
              <button
                type="button"
                className="menu-item danger"
                onClick={() => {
                  onEliminar?.(menuOpenId);
                  setMenuOpenId(null);
                  setMenuPosition(null);
                }}
                disabled={deleting}
              >
                <i className="bi bi-trash me-2" />
                Eliminar
              </button>
            </div>
          )}

          {showForm && (
            <div className="management-card bg-light border rounded p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">{editingId ? "Editar horario" : "Agregar nuevo horario"}</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                >
                  Cerrar
                </button>
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small text-muted">Nombre</label>
                  <select
                    className="form-control"
                    value={form.nombre}
                    onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                  >
                    <option value="">Selecciona un horario</option>
                    {SHIFT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small text-muted">Inicio</label>
                  <input
                    className="form-control"
                    type="time"
                    value={form.hora_inicio}
                    onChange={(e) => setForm((p) => ({ ...p, hora_inicio: e.target.value }))}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small text-muted">Fin</label>
                  <input
                    className="form-control"
                    type="time"
                    value={form.hora_fin}
                    onChange={(e) => setForm((p) => ({ ...p, hora_fin: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small text-muted">Dias de la semana</label>
                  <div className="d-flex flex-wrap gap-2">
                    {DAY_OPTIONS.map((day) => (
                      <React.Fragment key={day.value}>
                        <input
                          type="checkbox"
                          className="btn-check"
                          id={`dia-${day.value}`}
                          autoComplete="off"
                          checked={selectedDays.includes(day.value)}
                          onChange={() => toggleDay(day.value)}
                        />
                        <label className="btn btn-outline-secondary btn-sm" htmlFor={`dia-${day.value}`}>
                          {day.label}
                        </label>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="form-text">Selecciona al menos un dia.</div>
                </div>
                <div className="col-12 d-flex gap-2">
                  <button
                    type="button"
                    className="btn management-btn-primary flex-grow-1"
                    disabled={!canSubmit || saving}
                    onClick={handleCrear}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Guardando...
                      </>
                    ) : editingId ? (
                      <>
                        <i className="bi bi-check-circle me-2" />
                        Guardar cambios
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plus-circle me-2" />
                        Agregar horario
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
